import clsx from 'clsx';
import { useEditorRef } from 'platejs/react';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import WrapTextIcon from '@mui/icons-material/WrapText';
import styles from './ImageWrapMenu.module.scss';

export type ImageWrap = 'inline' | 'float-left' | 'float-right' | 'block-center';

const OPTIONS: Array<{
    value: ImageWrap;
    label: string;
    Icon: React.ComponentType<{ fontSize?: 'small' | 'medium' | 'large' }>;
}> = [
    { value: 'inline', label: 'Inline with text', Icon: WrapTextIcon },
    { value: 'float-left', label: 'Wrap left', Icon: FormatAlignLeftIcon },
    { value: 'float-right', label: 'Wrap right', Icon: FormatAlignRightIcon },
    { value: 'block-center', label: 'Break text', Icon: FormatAlignCenterIcon },
];

interface ImageWrapMenuProps {
    element: any;
    current: ImageWrap;
    // Floating bubble above the image (default) vs. flat inline used by the
    // main toolbar's redundant entry. Inline mode shares the toolbar's btn
    // hover/active styles for visual consistency.
    variant?: 'floating' | 'inline';
}

export function ImageWrapMenu({ element, current, variant = 'floating' }: ImageWrapMenuProps) {
    const editor = useEditorRef();
    const setWrap = (value: ImageWrap) => (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            const ed: any = editor;
            const path = ed?.api?.findPath?.(element);
            if (!path) return;
            ed?.tf?.setNodes?.({ imageWrap: value }, { at: path });
        } catch {
            /* defensive: editor not ready */
        }
    };

    return (
        <div
            className={clsx(variant === 'floating' ? styles.floating : styles.inline)}
            role="toolbar"
            aria-label="Image wrap"
            contentEditable={false}
            onMouseDown={(e) => e.preventDefault()}
        >
            {OPTIONS.map(({ value, label, Icon }) => (
                <button
                    key={value}
                    type="button"
                    className={clsx(styles.btn, current === value && styles.btnActive)}
                    onMouseDown={setWrap(value)}
                    title={label}
                    aria-label={label}
                    aria-pressed={current === value || undefined}
                    data-wrap={value}
                >
                    <Icon fontSize="small" />
                </button>
            ))}
        </div>
    );
}
